import React, { useState, useEffect } from 'react';
import { 
  Plus, Trash2, Check, X, Globe2, Search, Database, Users, Edit2, BarChart3, 
  CreditCard, Settings, RefreshCw, Activity, Zap, Shield, TrendingUp, Clock, 
  CheckCircle, DollarSign, FileText, Mic, Ban, UserCheck, Loader2, Eye, Download
} from 'lucide-react';
import { useAuth } from '../lib/authContext';

const API_BASE = import.meta.env.PROD ? 'https://izwi-api.orbxcode.workers.dev' : '';

type Tab = 'dashboard' | 'users' | 'languages' | 'jobs' | 'payouts' | 'vault' | 'config';

const AdminPanel: React.FC = () => {
  const [tab, setTab] = useState<Tab>('dashboard');
  const { user } = useAuth();
  
  const tabs = [
    { id: 'dashboard', icon: BarChart3, label: 'Dashboard' },
    { id: 'users', icon: Users, label: 'Users' },
    { id: 'languages', icon: Globe2, label: 'Languages' },
    { id: 'jobs', icon: Mic, label: 'Jobs' },
    { id: 'payouts', icon: CreditCard, label: 'Payouts' },
    { id: 'vault', icon: Database, label: 'Vault' },
    { id: 'config', icon: Settings, label: 'Config' },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-500" />
            Admin Control Center
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Logged in as {user?.email}</p>
        </div>
      </div>

      <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg p-1 flex-wrap gap-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id as Tab)}
            className={`px-3 py-1.5 rounded text-sm flex items-center gap-1.5 ${tab === t.id ? 'bg-purple-600 text-white' : 'text-zinc-400 hover:text-white'}`}>
            <t.icon className="w-4 h-4" /> <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'languages' && <LanguagesTab />}
      {tab === 'jobs' && <JobsTab />}
      {tab === 'payouts' && <PayoutsTab />}
      {tab === 'vault' && <VaultTab />}
      {tab === 'config' && <ConfigTab />}
    </div>
  );
};

// Dashboard
const DashboardTab: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/stats`);
      setStats(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) return <div className="text-zinc-500 text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const cards = [
    { label: 'Users', value: stats?.users || 0, icon: Users, color: 'text-blue-400' },
    { label: 'Annotations', value: stats?.annotations || 0, icon: FileText, color: 'text-green-400' },
    { label: 'Tokens Earned', value: stats?.total_tokens_earned || 0, icon: Zap, color: 'text-purple-400' },
    { label: 'Languages', value: stats?.languages || 0, icon: Globe2, color: 'text-yellow-400' },
    { label: 'Vault Items', value: stats?.vault?.items || 0, icon: Database, color: 'text-pink-400' },
    { label: 'Verified', value: `${stats?.segments?.verified || 0}/${stats?.segments?.total || 0}`, icon: CheckCircle, color: 'text-cyan-400' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Overview</h2>
        <button onClick={fetchStats} className="p-2 text-zinc-400 hover:text-white"><RefreshCw className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map(c => (
          <div key={c.label} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2"><c.icon className={`w-4 h-4 ${c.color}`} /><p className="text-zinc-500 text-xs uppercase">{c.label}</p></div>
            <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
          <h3 className="text-yellow-400 font-medium mb-2 flex items-center gap-2"><Clock className="w-4 h-4" />Pending Payouts</h3>
          <p className="text-3xl font-bold text-yellow-400">R{((stats?.payouts?.pending_total || 0) / 100).toFixed(2)}</p>
          <p className="text-zinc-500 text-sm">{stats?.payouts?.pending_count || 0} awaiting approval</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
          <h3 className="text-green-400 font-medium mb-2 flex items-center gap-2"><DollarSign className="w-4 h-4" />Total Paid</h3>
          <p className="text-3xl font-bold text-green-400">R{((stats?.payouts?.total_cents || 0) / 100).toFixed(2)}</p>
          <p className="text-zinc-500 text-sm">{stats?.payouts?.completed || 0} completed</p>
        </div>
      </div>
    </div>
  );
};


// Users Tab
const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const fetchUsers = async () => {
    setLoading(true);
    const res = await fetch(`${API_BASE}/api/admin/users`);
    setUsers(await res.json());
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleRoleChange = async (id: string, role: string) => {
    await fetch(`${API_BASE}/api/admin/users/${id}/role`, { 
      method: 'PATCH', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify({ role }) 
    });
    fetchUsers();
  };

  const handleBan = async (id: string, banned: boolean) => {
    await fetch(`${API_BASE}/api/admin/users/${id}/ban`, { 
      method: 'PATCH', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify({ banned }) 
    });
    fetchUsers();
  };

  const handleResetTokens = async (id: string) => {
    if (!confirm('Reset this user\'s tokens to 0?')) return;
    await fetch(`${API_BASE}/api/admin/users/${id}/reset-tokens`, { method: 'POST' });
    fetchUsers();
  };

  const handleAddBalance = async (id: string) => {
    const amount = prompt('Enter amount in cents to add:');
    if (!amount) return;
    await fetch(`${API_BASE}/api/wallet/${id}/credit`, { 
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify({ amount_cents: parseInt(amount), type: 'admin_credit', reference: 'Admin adjustment' }) 
    });
    fetchUsers();
  };

  const filtered = users.filter(u => !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-zinc-500" />
          <input type="text" placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} 
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg py-2 pl-10 pr-4 text-white" />
        </div>
        <button onClick={fetchUsers} className="px-4 py-2 bg-zinc-800 text-white rounded-lg"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-left text-xs text-zinc-500 uppercase">
            <tr><th className="p-3">User</th><th className="p-3">Clan/Rank</th><th className="p-3">Tokens</th><th className="p-3">Balance</th><th className="p-3">Trust</th><th className="p-3">Role</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? <tr><td colSpan={7} className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr> : 
            filtered.slice(0, 100).map(user => (
              <tr key={user.id} className="hover:bg-zinc-800/50">
                <td className="p-3">
                  <p className="text-white font-medium">{user.name}</p>
                  <p className="text-zinc-500 text-xs">{user.email}</p>
                </td>
                <td className="p-3 text-zinc-400 text-xs">{user.clan}<br/>{user.rank}</td>
                <td className="p-3 text-purple-400 font-mono">{user.tokens || 0}</td>
                <td className="p-3 text-green-400 font-mono">R{((user.balance_cents || 0) / 100).toFixed(2)}</td>
                <td className="p-3"><span className={`px-2 py-0.5 text-xs rounded-full ${user.trust_score >= 80 ? 'bg-green-500/10 text-green-400' : user.trust_score >= 50 ? 'bg-yellow-500/10 text-yellow-400' : 'bg-red-500/10 text-red-400'}`}>{user.trust_score || 0}%</span></td>
                <td className="p-3">
                  <select value={user.role} onChange={(e) => handleRoleChange(user.id, e.target.value)} 
                    className={`bg-zinc-800 border rounded px-2 py-1 text-xs ${user.role === 'admin' ? 'border-purple-500 text-purple-400' : 'border-zinc-700 text-zinc-300'}`}>
                    <option value="annotator">Annotator</option>
                    <option value="buyer">Buyer</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <button onClick={() => setSelectedUser(user)} className="p-1 text-zinc-400 hover:text-white rounded" title="View"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => handleAddBalance(user.id)} className="p-1 text-green-400 hover:bg-green-500/10 rounded" title="Add Balance"><DollarSign className="w-4 h-4" /></button>
                    <button onClick={() => handleResetTokens(user.id)} className="p-1 text-yellow-400 hover:bg-yellow-500/10 rounded" title="Reset Tokens"><RefreshCw className="w-4 h-4" /></button>
                    <button onClick={() => handleBan(user.id, !user.banned)} className={`p-1 rounded ${user.banned ? 'text-green-400 hover:bg-green-500/10' : 'text-red-400 hover:bg-red-500/10'}`} title={user.banned ? 'Unban' : 'Ban'}>
                      {user.banned ? <UserCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedUser(null)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4">User Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">ID:</span><span className="text-white font-mono">{selectedUser.id}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Name:</span><span className="text-white">{selectedUser.name}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Email:</span><span className="text-white">{selectedUser.email}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Role:</span><span className="text-purple-400">{selectedUser.role}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Clan:</span><span className="text-white">{selectedUser.clan}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Rank:</span><span className="text-white">{selectedUser.rank}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Tokens:</span><span className="text-purple-400">{selectedUser.tokens}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Balance:</span><span className="text-green-400">R{((selectedUser.balance_cents || 0) / 100).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Trust Score:</span><span className="text-white">{selectedUser.trust_score}%</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Joined:</span><span className="text-white">{selectedUser.created_at}</span></div>
            </div>
            <button onClick={() => setSelectedUser(null)} className="mt-4 w-full py-2 bg-zinc-800 text-white rounded-lg">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};


// Languages Tab
const LanguagesTab: React.FC = () => {
  const [languages, setLanguages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', level: 'language', country: '', iso_code: '', region: '' });

  const fetchLanguages = async () => { 
    const res = await fetch(`${API_BASE}/api/admin/languages`); 
    setLanguages(await res.json()); 
    setLoading(false); 
  };
  useEffect(() => { fetchLanguages(); }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`${API_BASE}/api/admin/languages`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    setForm({ code: '', name: '', level: 'language', country: '', iso_code: '', region: '' }); 
    setShowForm(false); 
    fetchLanguages();
  };

  const handleApprove = async (id: string) => { 
    await fetch(`${API_BASE}/api/admin/languages/${id}/approve`, { method: 'PATCH' }); 
    fetchLanguages(); 
  };
  
  const handleDelete = async (id: string) => { 
    if (confirm('Delete this language?')) { 
      await fetch(`${API_BASE}/api/admin/languages/${id}`, { method: 'DELETE' }); 
      fetchLanguages(); 
    } 
  };

  const filtered = languages.filter(l => !search || l.name?.toLowerCase().includes(search.toLowerCase()) || l.code?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-zinc-500" />
          <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg py-2 pl-10 pr-4 text-white" />
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center gap-2"><Plus className="w-4 h-4" /> Add</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <form onSubmit={handleAdd} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-bold text-white">Add Language</h3>
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="Code (e.g. zul)" value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
              <input placeholder="ISO Code" value={form.iso_code} onChange={e => setForm({...form, iso_code: e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
            </div>
            <input required placeholder="Name (e.g. Zulu)" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
            <select value={form.level} onChange={e => setForm({...form, level: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white">
              <option value="phylum">Phylum</option>
              <option value="family">Family</option>
              <option value="subgroup">Subgroup</option>
              <option value="language">Language</option>
              <option value="dialect">Dialect</option>
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="Country" value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
              <input placeholder="Region" value={form.region} onChange={e => setForm({...form, region: e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2 bg-zinc-800 text-zinc-300 rounded-lg">Cancel</button>
              <button type="submit" className="flex-1 py-2 bg-purple-600 text-white rounded-lg">Add</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-left text-xs text-zinc-500 uppercase">
            <tr><th className="p-3">Code</th><th className="p-3">Name</th><th className="p-3">Level</th><th className="p-3">Country</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? <tr><td colSpan={6} className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr> : 
            filtered.map(lang => (
              <tr key={lang.id} className="hover:bg-zinc-800/50">
                <td className="p-3 font-mono text-purple-400">{lang.code}</td>
                <td className="p-3 text-white">{lang.name}</td>
                <td className="p-3 text-zinc-400 text-xs uppercase">{lang.level}</td>
                <td className="p-3 text-zinc-400">{lang.country}</td>
                <td className="p-3"><span className={`px-2 py-0.5 text-xs rounded-full ${lang.is_approved ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{lang.is_approved ? 'Approved' : 'Pending'}</span></td>
                <td className="p-3 flex gap-1">
                  {!lang.is_approved && <button onClick={() => handleApprove(lang.id)} className="p-1 text-green-400 hover:bg-green-500/10 rounded"><Check className="w-4 h-4" /></button>}
                  <button onClick={() => handleDelete(lang.id)} className="p-1 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Jobs Tab
const JobsTab: React.FC = () => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchJobs = async () => {
    const url = filter === 'all' ? `${API_BASE}/api/admin/jobs` : `${API_BASE}/api/admin/jobs?status=${filter}`;
    const res = await fetch(url);
    setJobs(await res.json());
    setLoading(false);
  };
  useEffect(() => { fetchJobs(); }, [filter]);

  const handleRetry = async (id: string) => {
    await fetch(`${API_BASE}/api/admin/jobs/${id}/retry`, { method: 'POST' });
    fetchJobs();
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this job?')) {
      await fetch(`${API_BASE}/api/admin/jobs/${id}`, { method: 'DELETE' });
      fetchJobs();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'processing', 'completed', 'failed'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded text-sm ${filter === f ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <button onClick={fetchJobs} className="ml-auto px-3 py-1.5 bg-zinc-800 text-zinc-400 rounded"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-left text-xs text-zinc-500 uppercase">
            <tr><th className="p-3">ID</th><th className="p-3">Filename</th><th className="p-3">Status</th><th className="p-3">Progress</th><th className="p-3">Tier</th><th className="p-3">Created</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? <tr><td colSpan={7} className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr> : 
            jobs.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-zinc-500">No jobs found</td></tr> :
            jobs.map(job => (
              <tr key={job.id} className="hover:bg-zinc-800/50">
                <td className="p-3 font-mono text-xs text-zinc-400">{job.id.slice(0, 8)}...</td>
                <td className="p-3 text-white">{job.filename}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    job.status === 'completed' ? 'bg-green-500/10 text-green-400' : 
                    job.status === 'processing' ? 'bg-blue-500/10 text-blue-400' : 
                    job.status === 'failed' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'
                  }`}>{job.status}</span>
                </td>
                <td className="p-3 text-zinc-400">{job.processed_frames}/{job.total_frames}</td>
                <td className="p-3 text-zinc-400 text-xs uppercase">{job.tier}</td>
                <td className="p-3 text-zinc-500 text-xs">{job.created_at}</td>
                <td className="p-3 flex gap-1">
                  {job.status === 'failed' && <button onClick={() => handleRetry(job.id)} className="p-1 text-yellow-400 hover:bg-yellow-500/10 rounded"><RefreshCw className="w-4 h-4" /></button>}
                  <button onClick={() => handleDelete(job.id)} className="p-1 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// Payouts Tab
const PayoutsTab: React.FC = () => {
  const [payouts, setPayouts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState<string | null>(null);

  const fetchPayouts = async () => { 
    const url = filter === 'all' ? `${API_BASE}/api/admin/payouts` : `${API_BASE}/api/admin/payouts?status=${filter}`;
    const res = await fetch(url); 
    setPayouts(await res.json()); 
    setLoading(false); 
  };
  useEffect(() => { fetchPayouts(); }, [filter]);

  const handleApprove = async (id: string) => {
    const reference = prompt('Enter payment reference (e.g. Polar transaction ID):');
    if (!reference) return;
    setProcessing(id);
    await fetch(`${API_BASE}/api/admin/payouts/${id}`, { 
      method: 'PATCH', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify({ status: 'completed', reference }) 
    });
    setProcessing(null);
    fetchPayouts();
  };

  const handleReject = async (id: string) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    setProcessing(id);
    await fetch(`${API_BASE}/api/admin/payouts/${id}`, { 
      method: 'PATCH', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify({ status: 'rejected', reference: reason }) 
    });
    setProcessing(null);
    fetchPayouts();
  };

  const pendingTotal = payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.amount_cents || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          {['pending', 'completed', 'rejected', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded text-sm ${filter === f ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        {filter === 'pending' && pendingTotal > 0 && (
          <div className="text-yellow-400 font-mono">Total Pending: R{(pendingTotal / 100).toFixed(2)}</div>
        )}
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-left text-xs text-zinc-500 uppercase">
            <tr><th className="p-3">User</th><th className="p-3">Amount</th><th className="p-3">Method</th><th className="p-3">Reference</th><th className="p-3">Status</th><th className="p-3">Date</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? <tr><td colSpan={7} className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr> : 
            payouts.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-zinc-500">No payouts</td></tr> : 
            payouts.map(p => (
              <tr key={p.id} className="hover:bg-zinc-800/50">
                <td className="p-3"><p className="text-white">{p.user_name}</p><p className="text-zinc-500 text-xs">{p.user_email}</p></td>
                <td className="p-3 text-white font-mono font-bold">R{((p.amount_cents || 0) / 100).toFixed(2)}</td>
                <td className="p-3 text-zinc-400 uppercase text-xs">{p.method}</td>
                <td className="p-3 text-zinc-400 font-mono text-xs max-w-[150px] truncate">{p.reference || '-'}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    p.status === 'completed' ? 'bg-green-500/10 text-green-400' : 
                    p.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-red-500/10 text-red-400'
                  }`}>{p.status}</span>
                </td>
                <td className="p-3 text-zinc-500 text-xs">{p.created_at}</td>
                <td className="p-3">
                  {p.status === 'pending' && (
                    <div className="flex gap-1">
                      <button onClick={() => handleApprove(p.id)} disabled={processing === p.id} className="p-1 text-green-400 hover:bg-green-500/10 rounded disabled:opacity-50" title="Approve">
                        {processing === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleReject(p.id)} disabled={processing === p.id} className="p-1 text-red-400 hover:bg-red-500/10 rounded disabled:opacity-50" title="Reject">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Vault Tab
const VaultTab: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [form, setForm] = useState({ title: '', language_id: '', type: 'Multimodal', size_bytes: 0, audio_hours: 0, price_cents: 0, preview: '', afro_bench_score: 0, bias_score: 'A' });

  const fetchItems = async () => { const res = await fetch(`${API_BASE}/api/admin/vault`); setItems(await res.json()); setLoading(false); };
  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editItem) { 
      await fetch(`${API_BASE}/api/admin/vault/${editItem.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }); 
    } else { 
      await fetch(`${API_BASE}/api/admin/vault`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }); 
    }
    setForm({ title: '', language_id: '', type: 'Multimodal', size_bytes: 0, audio_hours: 0, price_cents: 0, preview: '', afro_bench_score: 0, bias_score: 'A' }); 
    setShowForm(false); 
    setEditItem(null); 
    fetchItems();
  };

  const handleEdit = (item: any) => { 
    setEditItem(item); 
    setForm({ title: item.title, language_id: item.language_id || '', type: item.type, size_bytes: item.size_bytes, audio_hours: item.audio_hours, price_cents: item.price_cents, preview: item.preview || '', afro_bench_score: item.afro_bench_score || 0, bias_score: item.bias_score || 'A' }); 
    setShowForm(true); 
  };
  
  const handleDelete = async (id: string) => { 
    if (confirm('Delete this dataset?')) { 
      await fetch(`${API_BASE}/api/admin/vault/${id}`, { method: 'DELETE' }); 
      fetchItems(); 
    } 
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <p className="text-zinc-400">{items.length} datasets in vault</p>
        <button onClick={() => { setEditItem(null); setForm({ title: '', language_id: '', type: 'Multimodal', size_bytes: 0, audio_hours: 0, price_cents: 0, preview: '', afro_bench_score: 0, bias_score: 'A' }); setShowForm(true); }} className="px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center gap-2"><Plus className="w-4 h-4" /> Add Dataset</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 w-full max-w-lg space-y-3">
            <h3 className="text-lg font-bold text-white">{editItem ? 'Edit' : 'Add'} Dataset</h3>
            <input required placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Language ID" value={form.language_id} onChange={e => setForm({...form, language_id: e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white">
                <option>Multimodal</option><option>Video-Text</option><option>Audio-Text</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input type="number" placeholder="Size (bytes)" value={form.size_bytes} onChange={e => setForm({...form, size_bytes: +e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
              <input type="number" step="0.1" placeholder="Hours" value={form.audio_hours} onChange={e => setForm({...form, audio_hours: +e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
              <input type="number" placeholder="Price (cents)" value={form.price_cents} onChange={e => setForm({...form, price_cents: +e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
            </div>
            <input placeholder="Preview text" value={form.preview} onChange={e => setForm({...form, preview: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
            <div className="grid grid-cols-2 gap-3">
              <input type="number" placeholder="AfroBench 0-100" value={form.afro_bench_score} onChange={e => setForm({...form, afro_bench_score: +e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white" />
              <select value={form.bias_score} onChange={e => setForm({...form, bias_score: e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white">
                <option>A</option><option>B</option><option>C</option><option>D</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => { setShowForm(false); setEditItem(null); }} className="flex-1 py-2 bg-zinc-800 text-zinc-300 rounded-lg">Cancel</button>
              <button type="submit" className="flex-1 py-2 bg-purple-600 text-white rounded-lg">{editItem ? 'Update' : 'Add'}</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid gap-4">
        {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></div> : 
        items.length === 0 ? <p className="text-zinc-500 text-center py-8">No datasets in vault</p> : 
        items.map(item => (
          <div key={item.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center justify-between">
            <div>
              <h3 className="text-white font-medium">{item.title}</h3>
              <p className="text-zinc-400 text-sm">{item.language_name || item.language_id} • {item.type} • {item.audio_hours}h</p>
              {item.afro_bench_score && <span className="text-xs text-purple-400">AfroBench: {item.afro_bench_score}</span>}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xl font-bold text-white">${(item.price_cents / 100).toFixed(2)}</p>
                <p className="text-xs text-zinc-500">{item.sold || 0} sold</p>
              </div>
              <button onClick={() => handleEdit(item)} className="p-2 text-zinc-400 hover:text-white rounded"><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(item.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// Config Tab
const ConfigTab: React.FC = () => {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchConfig = async () => { 
    const res = await fetch(`${API_BASE}/api/admin/config`); 
    setConfig(await res.json()); 
    setLoading(false); 
  };
  useEffect(() => { fetchConfig(); }, []);

  const updateConfig = async (key: string, value: string) => {
    setSaving(key);
    await fetch(`${API_BASE}/api/admin/config`, { 
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify({ key, value }) 
    });
    setConfig({ ...config, [key]: value });
    setSaving(null);
  };

  const configs = [
    { key: 'payout_threshold', label: 'Payout Threshold (tokens)', type: 'number', description: 'Minimum tokens before payout is triggered' },
    { key: 'tokens_per_annotation', label: 'Tokens per Annotation', type: 'number', description: 'Tokens earned per verified annotation' },
    { key: 'payout_amount_cents', label: 'Payout Amount (cents)', type: 'number', description: 'Amount paid when threshold is reached' },
    { key: 'min_trust_score', label: 'Min Trust Score', type: 'number', description: 'Minimum trust score for payouts' },
    { key: 'auto_verify_threshold', label: 'Auto-Verify Confidence', type: 'number', description: 'AI confidence threshold for auto-verification (0-100)' },
    { key: 'max_batch_size', label: 'Max Batch Size', type: 'number', description: 'Maximum segments per annotation batch' },
  ];

  const secretConfigs = [
    { key: 'gemini_api_key', label: 'Gemini API Key (Fallback)', type: 'password', description: 'Optional fallback AI provider' },
    { key: 'polar_secret', label: 'Polar Secret Key', type: 'password', description: 'Polar.sh secret key for payouts' },
  ];

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></div>;

  return (
    <div className="space-y-6">
      {/* App Settings */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Settings className="w-4 h-4 text-zinc-400" /> App Settings</h3>
        <div className="space-y-4">
          {configs.map(c => (
            <div key={c.key} className="flex items-center justify-between gap-4 py-2 border-b border-zinc-800 last:border-0">
              <div>
                <label className="text-zinc-300 text-sm font-medium">{c.label}</label>
                <p className="text-zinc-500 text-xs">{c.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type={c.type} 
                  value={config[c.key] || ''} 
                  onChange={e => updateConfig(c.key, e.target.value)} 
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white w-32 text-right" 
                />
                {saving === c.key && <Loader2 className="w-4 h-4 animate-spin text-purple-400" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Secret Keys */}
      <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
        <h3 className="text-red-400 font-medium mb-4 flex items-center gap-2"><Shield className="w-4 h-4" /> Secret Keys (Sensitive)</h3>
        <p className="text-zinc-500 text-sm mb-4">These are stored as Cloudflare Worker secrets. Update via wrangler CLI:</p>
        <code className="text-xs text-zinc-400 bg-black/30 p-2 rounded block mb-4">npx wrangler secret put GEMINI_API_KEY</code>
        <div className="space-y-4">
          {secretConfigs.map(c => (
            <div key={c.key} className="flex items-center justify-between gap-4 py-2 border-b border-red-500/10 last:border-0">
              <div>
                <label className="text-zinc-300 text-sm font-medium">{c.label}</label>
                <p className="text-zinc-500 text-xs">{c.description}</p>
              </div>
              <input 
                type="password" 
                value={config[c.key] || ''} 
                onChange={e => updateConfig(c.key, e.target.value)} 
                placeholder="••••••••"
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white w-48" 
              />
            </div>
          ))}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
        <h3 className="text-red-400 font-medium mb-4">Danger Zone</h3>
        <div className="space-y-3">
          <button 
            onClick={() => { if (confirm('Clear all pending payouts? This cannot be undone.')) { fetch(`${API_BASE}/api/admin/clear-pending-payouts`, { method: 'POST' }); } }}
            className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors"
          >
            Clear Pending Payouts
          </button>
          <button 
            onClick={() => { if (confirm('Reset all user tokens to 0? This cannot be undone.')) { fetch(`${API_BASE}/api/admin/reset-all-tokens`, { method: 'POST' }); } }}
            className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors ml-2"
          >
            Reset All Tokens
          </button>
        </div>
      </div>

      {/* System Info */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">System Info</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-zinc-500">API:</span> <span className="text-green-400">izwi-api.orbxcode.workers.dev</span></div>
          <div><span className="text-zinc-500">Database:</span> <span className="text-green-400">Cloudflare D1</span></div>
          <div><span className="text-zinc-500">AI:</span> <span className="text-green-400">Cloudflare Workers AI</span></div>
          <div><span className="text-zinc-500">Storage:</span> <span className="text-zinc-400">R2 (not configured)</span></div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
